// Deadline countdown derivation (extracted from GameweekGrid.tsx, where it
// arrived). Pure: given "now" and the gameweek's fixtures it
// answers what the header should print, so the thresholds are unit testable without a
// clock.
import { deadlineFromKickoffs } from '../../deadline'
import type { Game } from '../../types'

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const URGENT_THRESHOLD_MS = 12 * 60 * 60 * 1000
// Countdown display is minute-grained ("Xh Xm"), so a once-a-minute refresh
// is enough to keep it live without a per-second ticking interval.
export const COUNTDOWN_REFRESH_MS = 60 * 1000

/** Local-time start of the Monday that owns `ms`. Week boundary is Monday (en-GB / Europe
 *  convention, as everywhere else in the app), so "same week" is a calendar question, not a
 *  "within 7 days" one: a Wednesday deadline seen on the Monday after is a *different* week
 *  even though it is only 6 days out. */
export function startOfWeekMs(ms: number): number {
  const date = new Date(ms)
  date.setHours(0, 0, 0, 0)
  // getDay counts Sunday as 0; shift so Monday is 0 and Sunday closes the week at 6.
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7))
  return date.getTime()
}

export interface DeadlineCountdown {
  showCountdown: boolean
  isUrgent: boolean
  /** The line to render, or null when there is no countdown to show. */
  label: string | null
}

/** Derived through the shared `deadlineFromKickoffs` — 30 minutes before the
 *  gameweek's earliest kickoff — so this prints the same instant the server locks on. The
 *  client must never compute its own: two copies of that rule drift the first time one changes.
 *
 *  `now` is expected to be the *server's* clock (see useDeadlineCountdown), not `Date.now()`:
 *  a device running ten minutes fast must still count down to the instant everyone else does. */
export function computeDeadlineCountdown(
  games: Game[],
  now: number,
  deadlinePassed: boolean
): DeadlineCountdown {
  const deadlineMs = deadlineFromKickoffs(games.map((game) => new Date(game.kickoff).getTime()))
  const msUntilDeadline = deadlineMs !== null ? deadlineMs - now : null
  const showCountdown = !deadlinePassed && msUntilDeadline !== null && msUntilDeadline > 0
  if (!showCountdown || deadlineMs === null || msUntilDeadline === null) {
    return { showCountdown: false, isUrgent: false, label: null }
  }

  // More than a day out: show the actual deadline date/time rather than a
  // "Xh Xm" countdown — a raw hour count that far ahead reads as noise, not
  // urgency. Inside 24h, switch to the live countdown; inside 12h, that
  // countdown turns --urgent.
  if (msUntilDeadline > ONE_DAY_MS) {
    // Weekday + time alone is unambiguous only inside the current week. Past it the label
    // lies by omission — an international break puts the next deadline two-plus weeks out,
    // and "Deadline Fri 20:30" then reads as this Friday. Carry the date.
    const sameWeek = startOfWeekMs(deadlineMs) === startOfWeekMs(now)
    // Composed part by part rather than in one `toLocaleString` call: asking for weekday +
    // date + time together glues them with a locale connector ("Wed 20 Aug at 19:45" on some
    // ICU builds), and the printed shape is the contract here.
    const deadlineDate = new Date(deadlineMs)
    const weekday = deadlineDate.toLocaleDateString('en-GB', { weekday: 'short' })
    const time = deadlineDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    const dayAndMonth = deadlineDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    })
    const deadlineDateLabel = sameWeek ? `${weekday} ${time}` : `${weekday} ${dayAndMonth}, ${time}`
    return { showCountdown: true, isUrgent: false, label: `Deadline ${deadlineDateLabel}` }
  }

  const countdownMinutesTotal = Math.floor(msUntilDeadline / 60000)
  const countdownHours = Math.floor(countdownMinutesTotal / 60)
  const countdownMinutes = countdownMinutesTotal % 60
  return {
    showCountdown: true,
    isUrgent: msUntilDeadline <= URGENT_THRESHOLD_MS,
    label: `Deadline in ${countdownHours}h ${countdownMinutes}m`,
  }
}
